import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Lightbox, { type LightboxImage } from './Lightbox';
import { folderPath, searchFolderImages, subrequestKey } from './folder';
import { imageScaleUrl } from '../../../helpers/imageScales';
import './Gallery.scss';

type AspectRatio = '1:1' | '4:5' | '3:2' | 'mixed';
type Columns = 2 | 3 | 4;

interface FolderItem {
  '@id': string;
  title?: string;
  description?: string;
}

interface SelectionItem {
  image?: Array<{ '@id': string; title?: string }>;
}

const aspectClass = (aspect: AspectRatio) =>
  aspect === 'mixed' ? 'aspect-mixed' : `aspect-${aspect.replace(':', '-')}`;

const View = (props: any) => {
  const { data, isEditMode, imageSlot, properties, metadata } = props;
  const blockId: string = props.id ?? props.block;
  const mode = data.mode === 'selection' ? 'selection' : 'folder';
  const aspect: AspectRatio = ['1:1', '4:5', '3:2', 'mixed'].includes(
    data.aspect,
  )
    ? data.aspect
    : '1:1';
  const columns: Columns = [2, 3, 4].includes(Number(data.columns))
    ? (Number(data.columns) as Columns)
    : 3;
  const lightboxEnabled = data.lightbox !== false;

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const dispatch = useDispatch();
  const path = folderPath(data, properties || metadata);
  const result = useSelector(
    (state: any) => state.search?.subrequests?.[subrequestKey(blockId)],
  );
  const folderItems: FolderItem[] = useMemo(
    () =>
      path !== undefined && Array.isArray(result?.items) ? result.items : [],
    [path, result?.items],
  );

  // The server loads the images with getAsyncData. The browser loads them
  // again after a client-side navigation and when the folder changes.
  useEffect(() => {
    if (path === undefined) return;
    dispatch(searchFolderImages(path, blockId) as any);
  }, [dispatch, path, blockId]);

  const images: LightboxImage[] = useMemo(() => {
    if (mode === 'folder') {
      return folderItems.map((it) => ({
        src: imageScaleUrl(it, 'great'),
        alt: it.title || it.description || '',
      }));
    }
    const list: SelectionItem[] = Array.isArray(data.images) ? data.images : [];
    return list
      .map((row) => {
        const ref = Array.isArray(row.image) ? row.image[0] : undefined;
        if (!ref?.['@id']) return null;
        return {
          src: imageScaleUrl(ref, 'great'),
          alt: ref.title || '',
        } as LightboxImage;
      })
      .filter((x): x is LightboxImage => x !== null);
  }, [mode, folderItems, data.images]);

  const thumbSize =
    columns === 4 ? 'preview' : columns === 3 ? 'large' : 'great';

  type Thumb = {
    src: string | null;
    alt: string;
    rowIndex?: number;
    rawImage?: any;
  };

  const thumbs: Thumb[] = useMemo(() => {
    if (mode === 'folder') {
      return folderItems.map((it) => ({
        src: imageScaleUrl(it, thumbSize),
        alt: it.title || it.description || '',
      }));
    }
    const list: SelectionItem[] = Array.isArray(data.images) ? data.images : [];
    if (isEditMode) {
      // Surface every row in edit mode (including empty), so the inline
      // image input has a per-row slot.
      return list.map((row, i) => {
        const ref = Array.isArray(row.image) ? row.image[0] : undefined;
        return {
          src: ref?.['@id'] ? imageScaleUrl(ref, thumbSize) : null,
          alt: ref?.title || '',
          rowIndex: i,
          rawImage: row.image,
        };
      });
    }
    return list
      .map((row) => {
        const ref = Array.isArray(row.image) ? row.image[0] : undefined;
        if (!ref?.['@id']) return null;
        return {
          src: imageScaleUrl(ref, thumbSize),
          alt: ref.title || '',
        } as Thumb;
      })
      .filter((x): x is Thumb => x !== null);
  }, [mode, folderItems, data.images, thumbSize, isEditMode]);

  if (thumbs.length === 0) {
    if (!isEditMode) return null;
    return (
      <section className="gallery gallery--empty">
        <div className="shell">
          <p className="gallery-placeholder">
            {mode === 'folder'
              ? 'Aucune image dans ce dossier. Ajoutez des images à la page, ou choisissez un autre dossier dans la barre latérale.'
              : 'Ajoutez des images dans la barre latérale.'}
          </p>
        </div>
      </section>
    );
  }

  const onThumbClick = (index: number) => {
    if (!lightboxEnabled || isEditMode) return;
    setLightboxIndex(index);
  };

  return (
    <section className="gallery">
      <div className="shell">
        <div
          className={`gallery-grid gallery-grid--cols-${columns} gallery-grid--${aspectClass(aspect)}`}
        >
          {thumbs.map((img, i) => {
            const interactive = lightboxEnabled && !isEditMode;
            const Tag: any = interactive ? 'button' : 'div';
            const extraProps = interactive
              ? {
                  type: 'button',
                  onClick: () => onThumbClick(i),
                  'aria-label': img.alt || `Image ${i + 1}`,
                }
              : {};
            const defaultElement = img.src ? (
              <img src={img.src} alt={img.alt} loading="lazy" />
            ) : null;
            const slotted =
              isEditMode &&
              mode !== 'folder' &&
              typeof img.rowIndex === 'number'
                ? imageSlot?.({
                    fieldId: 'images',
                    index: img.rowIndex,
                    value: img.rawImage,
                    element: defaultElement,
                  })
                : undefined;
            return (
              <Tag
                key={`${img.src ?? 'empty'}-${i}`}
                className={`gallery-cell${interactive ? ' is-interactive' : ''}`}
                {...extraProps}
              >
                {slotted ?? defaultElement}
              </Tag>
            );
          })}
        </div>
      </div>
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </section>
  );
};

export default View;
